import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { PreservationsAPI } from "./preservations.api";
import {
  CreatePreservationData,
  CreatePreservationResult,
  DeletePreservationData,
  DeletePreservationResult,
  GetPreservationsQuery,
  UpdatePreservationData,
  UpdatePreservationResult,
} from "./preservations.types";

const preservationsQueryKeys = {
  base: ["preservations"],
  all: ["preservations", "list"],
  list: (q: GetPreservationsQuery) => ["preservations", "list", q],
  infiniteList: (q: GetPreservationsQuery) => [
    "preservations",
    "infinite-list",
    q,
  ],
};

const useGetInfinitePreservations = (
  query: GetPreservationsQuery,
  options?: { enabled?: boolean }
) => {
  return useInfiniteQuery({
    queryKey: preservationsQueryKeys.infiniteList(query),
    queryFn: ({ pageParam }) =>
      PreservationsAPI.get({
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.next_page;
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage?.page) return null;
      if (firstPage?.page - 1 === 0) return null;
      return firstPage?.page - 1;
    },
    initialPageParam: 1,
    placeholderData: (data) => data,
    ...options,
  });
};

const useUpdatePreservation = (options: {
  onSuccess?: (data: UpdatePreservationResult) => void;
  onError?: (error: unknown) => void;
}) => {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePreservationData) => {
      return PreservationsAPI.update(data);
    },
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: preservationsQueryKeys.base,
      });
    },
  });
};

const useCreatePreservation = (options?: {
  onSuccess?: (data: CreatePreservationResult) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePreservationData) => {
      return PreservationsAPI.create(data);
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: preservationsQueryKeys.base,
      });
    },
  });
};

const useDeletePreservation = (options?: {
  onSuccess?: (data: DeletePreservationResult) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeletePreservationData) => {
      return PreservationsAPI.delete(data);
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: preservationsQueryKeys.base,
      });
    },
  });
};

export {
  preservationsQueryKeys,
  useGetInfinitePreservations,
  useUpdatePreservation,
  useCreatePreservation,
  useDeletePreservation,
};
